import { fetchWithAuth, BASE_URL } from "../api/client";

/**
 * Mailing List Service
 * Handles all API calls for direct mail / mailing lists feature
 */

// Contact status constants
export const CONTACT_STATUS = {
  PENDING: "pending",
  VERIFIED: "verified",
  UNDELIVERABLE: "undeliverable",
  SENT: "sent",
  DELIVERED: "delivered",
  FAILED: "failed"
};

// Postcard size options with pricing
export const POSTCARD_SIZES = [
  { id: "4x6", name: "4×6", description: "Standard postcard", price: 0.70 },
  { id: "6x9", name: "6×9", description: "Large postcard", price: 1.00 },
  { id: "6x11", name: "6×11", description: "Jumbo postcard", price: 1.25 }
];

// Data source options
export const DATA_SOURCES = [
  { id: "dallas_cad", label: "Dallas CAD", description: "Dallas Central Appraisal District" },
  { id: "harris_cad", label: "Harris CAD", description: "Harris County Appraisal District" },
  { id: "manual", label: "Manual Entry", description: "Manually added contacts" },
  { id: "csv_import", label: "CSV Import", description: "Imported from CSV file" }
];

// County options for filtering
export const COUNTIES = [
  { id: "Harris", label: "Harris County", metro: "Houston Metro" },
  { id: "Dallas", label: "Dallas County", metro: "DFW Metro" }
];

// Status display configuration for badges
export const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    bgClass: "bg-slate-100 dark:bg-slate-700",
    textClass: "text-slate-600 dark:text-slate-300"
  },
  verified: {
    label: "Verified",
    bgClass: "bg-green-100 dark:bg-green-900/30",
    textClass: "text-green-600 dark:text-green-400"
  },
  undeliverable: {
    label: "Undeliverable",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-600 dark:text-red-400"
  },
  sent: {
    label: "Sent",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-600 dark:text-blue-400"
  },
  delivered: {
    label: "Delivered",
    bgClass: "bg-purple-100 dark:bg-purple-900/30",
    textClass: "text-purple-600 dark:text-purple-400"
  },
  failed: {
    label: "Failed",
    bgClass: "bg-orange-100 dark:bg-orange-900/30",
    textClass: "text-orange-600 dark:text-orange-400"
  }
};

// ==================== MAILING LIST CRUD ====================

/**
 * Fetch all mailing lists
 * @param {Object} filters - Optional filters
 * @param {string} filters.search - Search term for list names
 * @param {string} filters.county - Filter by county
 * @param {string} filters.source - Filter by data source
 * @param {string} filters.status - Filter by status (active, archived)
 * @param {number} filters.limit - Max results to return
 * @param {number} filters.offset - Pagination offset
 * @returns {Promise<{lists: MailingList[], total_count: number}>}
 */
export const fetchMailingLists = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.search) params.append("search", filters.search);
  if (filters.county) params.append("county", filters.county);
  if (filters.source) params.append("source", filters.source);
  if (filters.status) params.append("status", filters.status);
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.offset) params.append("offset", filters.offset.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch mailing lists");
  }

  return response.json();
};

/**
 * Fetch a single mailing list by ID
 * @param {string} listId
 * @returns {Promise<MailingList>}
 */
export const fetchMailingList = async (listId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}`, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Mailing list not found");
    }
    throw new Error("Failed to fetch mailing list");
  }

  return response.json();
};

/**
 * Create a new mailing list
 * @param {Object} data - List data
 * @param {string} data.name - List name
 * @param {string} data.description - Optional description
 * @param {string} data.source - Data source (dallas_cad, harris_cad, manual, csv_import)
 * @returns {Promise<MailingList>}
 */
export const createMailingList = async (data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to create mailing list");
  }

  return response.json();
};

/**
 * Update a mailing list
 * @param {string} listId
 * @param {Object} data - Fields to update
 * @returns {Promise<MailingList>}
 */
export const updateMailingList = async (listId, data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update mailing list");
  }

  return response.json();
};

/**
 * Delete a mailing list
 * @param {string} listId
 * @returns {Promise<{success: boolean}>}
 */
export const deleteMailingList = async (listId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to delete mailing list");
  }

  return response.json();
};

// ==================== CONTACTS ====================

/**
 * Fetch contacts for a mailing list
 * @param {string} listId
 * @param {Object} filters - Optional filters
 * @param {string} filters.status - Filter by status
 * @param {string} filters.search - Search term (name, address, phone)
 * @param {string} filters.city - Filter by city
 * @param {string} filters.zip - Filter by ZIP code prefix
 * @param {boolean} filters.has_phone - Only contacts with phone numbers
 * @param {number} filters.page - Page number (1-indexed)
 * @param {number} filters.pageSize - Results per page
 * @returns {Promise<{contacts: Contact[], total_count: number, page: number, page_size: number}>}
 */
export const fetchContacts = async (listId, filters = {}) => {
  const params = new URLSearchParams();

  if (filters.status) params.append("status", filters.status);
  if (filters.search) params.append("q", filters.search);
  if (filters.city) params.append("city", filters.city);
  if (filters.zip) params.append("zip", filters.zip);
  if (filters.has_phone) params.append("has_phone", "true");
  if (filters.page) params.append("page", filters.page.toString());
  if (filters.pageSize) params.append("page_size", filters.pageSize.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists/${listId}/contacts${queryString ? `?${queryString}` : ""}`;

  console.log('[MailingListService] fetchContacts:', { filters, url });

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch contacts");
  }

  return response.json();
};

/**
 * Create a single contact
 * @param {string} listId
 * @param {Object} data - Contact data
 * @returns {Promise<Contact>}
 */
export const createContact = async (listId, data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/contacts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to create contact");
  }

  return response.json();
};

/**
 * Update a contact
 * @param {string} listId
 * @param {string} contactId
 * @param {Object} data - Fields to update
 * @returns {Promise<Contact>}
 */
export const updateContact = async (listId, contactId, data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/contacts/${contactId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update contact");
  }

  return response.json();
};

/**
 * Delete a contact
 * @param {string} listId
 * @param {string} contactId
 * @returns {Promise<{success: boolean}>}
 */
export const deleteContact = async (listId, contactId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/contacts/${contactId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to delete contact");
  }

  return response.json();
};

/**
 * Verify a contact address via Lob API
 * @param {string} listId
 * @param {string} contactId
 * @returns {Promise<Contact>}
 */
export const verifyContact = async (listId, contactId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/contacts/${contactId}/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to verify address");
  }

  return response.json();
};

/**
 * Delete multiple contacts
 * @param {string} listId
 * @param {string[]} contactIds
 * @returns {Promise<{success: boolean, deleted_count: number}>}
 */
export const deleteContacts = async (listId, contactIds) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/contacts/bulk-delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contact_ids: contactIds })
  });

  if (!response.ok) {
    throw new Error("Failed to delete contacts");
  }

  return response.json();
};

// ==================== CSV IMPORT ====================

/**
 * Import contacts from a CSV file
 * @param {string} listId
 * @param {File} file - The CSV file
 * @param {Object} columnMapping - Map CSV columns to contact fields
 * @param {string} columnMapping.name - CSV column for contact name
 * @param {string} columnMapping.address_line1 - CSV column for address line 1
 * @param {string} columnMapping.address_line2 - CSV column for address line 2 (optional)
 * @param {string} columnMapping.city - CSV column for city
 * @param {string} columnMapping.state - CSV column for state
 * @param {string} columnMapping.zip - CSV column for zip code
 * @param {string} columnMapping.phone - CSV column for phone (optional)
 * @param {string} columnMapping.email - CSV column for email (optional)
 * @returns {Promise<{success: boolean, imported_count: number, failed_count: number, errors: string[]}>}
 */
export const importCSVFile = async (listId, file, columnMapping) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("column_mapping", JSON.stringify(columnMapping));

  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/import/file`, {
    method: "POST",
    body: formData
    // Don't set Content-Type header - browser will set it with boundary for FormData
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to import CSV");
  }

  return response.json();
};

/**
 * Parse a raw address string into structured components
 * @param {string} rawAddress
 * @returns {Promise<{address_line1: string, address_line2?: string, city: string, state: string, zip: string}>}
 */
export const parseAddress = async (rawAddress) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/parse-address`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ address: rawAddress })
  });

  if (!response.ok) {
    throw new Error("Failed to parse address");
  }

  return response.json();
};

// ==================== SEND POSTCARDS ====================

/**
 * Send postcards to contacts in a mailing list
 * @param {string} listId
 * @param {Object} options
 * @param {string} options.size - Postcard size (4x6, 6x9, 6x11)
 * @param {string} options.template_id - Template ID for postcard design
 * @param {boolean} options.dry_run - If true, simulate sending without actually sending
 * @param {string[]} options.contact_ids - Specific contacts to send to (optional, defaults to all eligible)
 * @returns {Promise<{success: boolean, sent_count: number, failed_count: number, total_cost: number, results: SendResult[]}>}
 */
export const sendPostcards = async (listId, options) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(options)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 402) {
      throw new Error(errorData.message || "Insufficient credits to send postcards");
    }
    throw new Error(errorData.message || "Failed to send postcards");
  }

  return response.json();
};

/**
 * Get send history for a mailing list
 * @param {string} listId
 * @param {Object} filters
 * @param {number} filters.limit
 * @param {number} filters.offset
 * @returns {Promise<{campaigns: Campaign[], total_count: number}>}
 */
export const fetchSendHistory = async (listId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.offset) params.append("offset", filters.offset.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists/${listId}/send-history${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch send history");
  }

  return response.json();
};

// ==================== TEMPLATES ====================

/**
 * Fetch available postcard templates
 * @returns {Promise<{templates: Template[]}>}
 */
export const fetchPostcardTemplates = async () => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/templates`, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch templates");
  }

  return response.json();
};

// ==================== GLOBAL SEARCH & STATS ====================

/**
 * Global search across all mailing lists
 * @param {Object} filters - Search filters
 * @param {string} filters.q - Search query (name, address, phone)
 * @param {string} filters.county - Filter by county (Harris, Dallas)
 * @param {string} filters.city - Filter by city
 * @param {string} filters.zip - Filter by ZIP code prefix
 * @param {string} filters.status - Filter by status
 * @param {boolean} filters.has_phone - Only contacts with phone numbers
 * @param {number} filters.limit - Max results (default: 100, max: 500)
 * @param {string} filters.page_key - Pagination cursor
 * @returns {Promise<{contacts: Contact[], total: number, filters_applied: Object, next_page_key: string|null}>}
 */
export const searchAllContacts = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.q) params.append("q", filters.q);
  if (filters.county) params.append("county", filters.county);
  if (filters.city) params.append("city", filters.city);
  if (filters.zip) params.append("zip", filters.zip);
  if (filters.status) params.append("status", filters.status);
  if (filters.has_phone) params.append("has_phone", "true");
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.page_key) params.append("page_key", filters.page_key);

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists/contacts/search${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to search contacts");
  }

  return response.json();
};

/**
 * Fetch aggregated stats by county and status
 * @returns {Promise<{total_lists: number, total_contacts: number, by_county: Object, by_status: Object}>}
 */
export const fetchMailingStats = async () => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/stats`, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch mailing stats");
  }

  return response.json();
};

/**
 * Export contacts to CSV
 * @param {string} listId - Optional list ID, omit to export all
 * @param {Object} filters - Optional filters
 * @param {string} filters.county - Filter by county
 * @param {string} filters.status - Filter by status
 * @returns {Promise<Blob>} CSV file blob
 */
export const exportContacts = async (listId = null, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.county) params.append("county", filters.county);
  if (filters.status) params.append("status", filters.status);

  const queryString = params.toString();
  let url;
  if (listId) {
    url = `${BASE_URL}/api/mailing-lists/${listId}/export${queryString ? `?${queryString}` : ""}`;
  } else {
    url = `${BASE_URL}/api/mailing-lists/contacts/export${queryString ? `?${queryString}` : ""}`;
  }

  const response = await fetchWithAuth(url, {
    headers: { "Accept": "text/csv" }
  });

  if (!response.ok) {
    throw new Error("Failed to export contacts");
  }

  return response.blob();
};

/**
 * Verify all pending addresses in a mailing list
 * @param {string} listId
 * @returns {Promise<{verified: number, undeliverable: number, errors: number}>}
 */
export const verifyAllContacts = async (listId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/${listId}/verify-all`, {
    method: "POST",
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to verify addresses");
  }

  return response.json();
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Format address for display
 * @param {Object} contact - Contact object with address fields
 * @returns {string}
 */
export const formatAddress = (contact) => {
  if (!contact) return "";

  const parts = [];
  if (contact.address_line1) parts.push(contact.address_line1);
  if (contact.address_line2) parts.push(contact.address_line2);

  // Support both naming conventions: address_city/state/zip and city/state/zip
  const city = contact.address_city || contact.city;
  const state = contact.address_state || contact.state;
  const zip = contact.address_zip || contact.zip;

  const cityStateZip = [city, state, zip].filter(Boolean).join(", ");

  if (cityStateZip) parts.push(cityStateZip);

  return parts.join(", ");
};

/**
 * Format date for display
 * @param {string} dateStr - ISO timestamp
 * @returns {string}
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

/**
 * Format relative time
 * @param {string} dateStr - ISO timestamp
 * @returns {string}
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return formatDate(dateStr);
};

/**
 * Format currency
 * @param {number} value
 * @returns {string}
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2
  }).format(value || 0);
};

/**
 * Parse CSV text into rows
 * @param {string} csvText
 * @returns {string[][]} Array of rows, each row is array of cell values
 */
export const parseCSV = (csvText) => {
  const lines = csvText.split(/\r?\n/);
  const rows = [];

  for (const line of lines) {
    if (!line.trim()) continue;

    const cells = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        cells.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    cells.push(current.trim());
    rows.push(cells);
  }

  return rows;
};

// ==================== CAMPAIGNS ====================

/**
 * Get ZIP code statistics for contact selection
 * @param {string} listId
 * @returns {Promise<{list_id: string, total_contacts: number, zip_codes: ZipCodeStats[]}>}
 */
export const fetchZipCodeStats = async (listId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/campaigns/lists/${listId}/zip-codes`, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch ZIP code statistics");
  }

  return response.json();
};

/**
 * Create a new campaign
 * @param {Object} data - Campaign data
 * @param {string} data.name - Campaign name
 * @param {string} data.list_id - Mailing list ID
 * @param {Object} data.selection - Contact selection criteria
 * @param {string} data.postcard_size - Postcard size (4x6, 6x9, 6x11)
 * @param {string} data.template_id - Template ID (optional)
 * @param {string} data.qr_code_url - QR code URL (optional)
 * @returns {Promise<Campaign>}
 */
export const createCampaign = async (data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/campaigns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || "Failed to create campaign");
  }

  return response.json();
};

/**
 * List user's campaigns
 * @param {Object} filters
 * @param {string} filters.list_id - Filter by mailing list
 * @param {string} filters.status - Filter by status
 * @param {number} filters.limit - Max results
 * @param {string} filters.page_key - Pagination key
 * @returns {Promise<{campaigns: Campaign[], total: number, next_page_key: string|null}>}
 */
export const fetchCampaigns = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.list_id) params.append("list_id", filters.list_id);
  if (filters.status) params.append("status", filters.status);
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.page_key) params.append("page_key", filters.page_key);

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists/campaigns${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch campaigns");
  }

  return response.json();
};

/**
 * Get campaign details
 * @param {string} campaignId
 * @returns {Promise<Campaign>}
 */
export const fetchCampaign = async (campaignId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/campaigns/${campaignId}`, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Campaign not found");
    }
    throw new Error("Failed to fetch campaign");
  }

  return response.json();
};

/**
 * Send a campaign
 * @param {string} campaignId
 * @param {Object} options
 * @param {boolean} options.dry_run - If true, preview only
 * @returns {Promise<CampaignSendResult>}
 */
export const sendCampaign = async (campaignId, options = {}) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/campaigns/${campaignId}/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(options)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 402) {
      throw new Error(errorData.detail || "Insufficient credits");
    }
    throw new Error(errorData.detail || "Failed to send campaign");
  }

  return response.json();
};

/**
 * Delete a draft campaign
 * @param {string} campaignId
 * @returns {Promise<void>}
 */
export const deleteCampaign = async (campaignId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/campaigns/${campaignId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || "Failed to delete campaign");
  }
};

/**
 * Get campaign contacts with delivery status
 * @param {string} campaignId
 * @param {Object} filters
 * @returns {Promise<{contacts: CampaignContact[], total: number}>}
 */
export const fetchCampaignContacts = async (campaignId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append("status", filters.status);
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.page_key) params.append("page_key", filters.page_key);

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists/campaigns/${campaignId}/contacts${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch campaign contacts");
  }

  return response.json();
};

/**
 * Get user's campaign dashboard metrics
 * @returns {Promise<DashboardMetrics>}
 */
export const fetchCampaignDashboard = async () => {
  const response = await fetchWithAuth(`${BASE_URL}/api/mailing-lists/campaigns/dashboard/metrics`, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch dashboard metrics");
  }

  return response.json();
};

// Campaign status display configuration
export const CAMPAIGN_STATUS_CONFIG = {
  draft: {
    label: "Draft",
    bgClass: "bg-slate-100 dark:bg-slate-700",
    textClass: "text-slate-600 dark:text-slate-300"
  },
  sending: {
    label: "Sending",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-600 dark:text-amber-400"
  },
  sent: {
    label: "Sent",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-600 dark:text-blue-400"
  },
  partial: {
    label: "Partial",
    bgClass: "bg-orange-100 dark:bg-orange-900/30",
    textClass: "text-orange-600 dark:text-orange-400"
  },
  failed: {
    label: "Failed",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-600 dark:text-red-400"
  },
  cancelled: {
    label: "Cancelled",
    bgClass: "bg-slate-100 dark:bg-slate-700",
    textClass: "text-slate-500 dark:text-slate-400"
  }
};

// Delivery status configuration
export const DELIVERY_STATUS_CONFIG = {
  pending: { label: "Pending", color: "slate" },
  sent: { label: "Sent", color: "blue" },
  in_transit: { label: "In Transit", color: "amber" },
  in_local_area: { label: "Near Delivery", color: "purple" },
  delivered: { label: "Delivered", color: "green" },
  returned: { label: "Returned", color: "red" }
};

// ==================== ANALYTICS ====================

/**
 * Get comprehensive campaign analytics
 * @param {Object} filters
 * @param {string} filters.list_id - Filter by mailing list
 * @param {number} filters.days - Number of days to analyze (default: 30)
 * @returns {Promise<CampaignAnalytics>}
 */
export const fetchCampaignAnalytics = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.list_id) params.append("list_id", filters.list_id);
  if (filters.days) params.append("days", filters.days.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists/campaigns/analytics${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch campaign analytics");
  }

  return response.json();
};

/**
 * Get mail history for a specific contact
 * @param {string} contactId
 * @param {number} limit
 * @returns {Promise<{contact_id: string, total_mailings: number, history: MailHistoryItem[]}>}
 */
export const fetchContactMailHistory = async (contactId, limit = 50) => {
  const response = await fetchWithAuth(
    `${BASE_URL}/api/mailing-lists/campaigns/contacts/${contactId}/mail-history?limit=${limit}`,
    {
      headers: { "Content-Type": "application/json" }
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch contact mail history");
  }

  return response.json();
};

/**
 * Get contacts with mail counts
 * @param {string} listId
 * @param {Object} filters
 * @returns {Promise<{contacts: ContactWithMailCount[], total_count: number, page: number, page_size: number, total_pages: number}>}
 */
export const fetchContactsWithMailCount = async (listId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.page) params.append("page", filters.page.toString());
  if (filters.pageSize) params.append("page_size", filters.pageSize.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.city) params.append("city", filters.city);
  if (filters.status) params.append("status", filters.status);

  const queryString = params.toString();
  const url = `${BASE_URL}/api/mailing-lists/campaigns/lists/${listId}/contacts-with-mail-count${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: { "Content-Type": "application/json" }
  });

  if (!response.ok) {
    throw new Error("Failed to fetch contacts with mail count");
  }

  return response.json();
};
